import { ChannelService } from '../../../../../services/channel.service';
import { SelectType } from '../../../../select/select.interface';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy,
} from '@angular/core';
import { EMPTY, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseService } from '../../../../../services/base.service';
import { ChannelForm } from './create-channel.interface';
import { trimValidator } from '../../../../../helpers';
import { IError, IFunnel } from '../../../../../interfaces';
import {
  CHANNEL_TYPE,
  CHANNEL_TYPES_OBJECT,
  DEFAULT_ERROR,
  ERROR_MESSAGES,
  FUNNEL_TYPE,
  activeTranslations,
  PERMISSIONS,
  SUCCESS_RESPONSE,
} from '../../../../../enum/constants';
import { NotificationService } from '../../../../../services/notifications.service';
import { IntegrationService } from '../../../../../services/integration.service';
import * as _ from 'lodash';
import { ChannelType } from '../../../../../modules/crm/channel/components/channel/channel.interface';
import { BoardService } from '../../../../../services/board.service';
import { environment } from '../../../../../../environments/environment';
import { StackedSidebarService } from '../../../../../services/stacked-sidebar.service';
import { SidebarType } from '../../../stacked-sidebar/stacked-sidebar.interface';
import { ChannelTypesResult } from '../../../stacked-sidebar/components/channel-types/channel-types.interface';
import { ChannelResponsibleEmployee, IChannelGroup } from '../../../../../model/channel.model';

@Component({
  selector: 'app-create-channel',
  templateUrl: './create-channel.component.html',
  styleUrl: './create-channel.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CreateChannelComponent implements OnInit, OnDestroy {
  // Destroy
  private readonly destroy$ = new Subject<null>();
  private readonly stackedSidebarService = inject(StackedSidebarService);

  // Form
  public channelForm!: FormGroup<ChannelForm>;
  private initialFormValue?: ReturnType<FormGroup<ChannelForm>['getRawValue']>;

  // Var`s
  public isLoader: boolean = false;
  public isDisabled: boolean = false;
  public isHasChange: boolean = false;

  public channel_id: string = '';
  public channel_type: string = '';
  public channel!: any;
  public allowedFunnels: IFunnel[] = [];
  public allowedColumns: any[] = [];

  public managersList: ChannelResponsibleEmployee[] = [];
  protected responsibleLoading = false;
  protected responsibleLoadError = false;
  protected canSelectDefaultResponsible = false;
  protected openingGroup = false;
  protected readonly SelectType = SelectType;

  @Input() payload: any;
  @Output() close = new EventEmitter<any>();

  constructor(
    readonly baseService: BaseService,
    readonly notificationService: NotificationService,
    readonly integrationService: IntegrationService,
    readonly boardService: BoardService,
    private readonly channelService: ChannelService,
  ) {}

  ngOnInit(): void {
    this.channel_id = this.payload?.channel_id;
    this.channel_type = this.payload?.channel_type;
    this.canSelectDefaultResponsible = this.baseService.isHasPermission(PERMISSIONS.CHANNELS_ACTION);
    this.isLoader = true;
    if (this.channel_id) this.onGetChannel();
    else this.onInitForm();
    this.onGetAllFunnels();
    if (this.canSelectDefaultResponsible) this.loadResponsibleEmployees();
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  protected get selectedChannelType(): ChannelType | null {
    const type = this.channelForm?.controls.type.value;
    if (!type) return null;

    const meta = CHANNEL_TYPES_OBJECT[type as CHANNEL_TYPE];

    return { value: type, name: meta?.name || type, img: meta?.img_url };
  }

  protected openChannelTypesSidebar(): void {
    this.channelForm.controls.type.markAsTouched();

    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.ChannelTypes,
          width: '640px',
          maxWidth: '95vw',
        },
        { selectedType: this.channelForm.controls.type.value || null },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: ChannelTypesResult | undefined) => {
        if (!result?.type) return;

        this.channelForm.controls.type.setValue(result.type);
        this.channelForm.controls.type.markAsDirty();
        this.selectDisable(result.type);
      });
  }

  private onInitForm(): void {
    if (this.channel_id) {
      this.channelForm = new FormGroup({
        name: new FormControl(this.channel.name, [Validators.required, trimValidator]),
        type: new FormControl(this.channel.type, [Validators.required]),
        ...(this.canSelectDefaultResponsible || this.channel?.channel_group_id
          ? { responsible_ids: new FormControl<string[]>(
              { value: this.channel?.responsible_ids || [], disabled: !!this.channel?.channel_group_id },
              { nonNullable: true },
            ) }
          : {}),
        funnel_id: new FormControl(this.channel?.funnel_id),
        column_id: new FormControl(this.channel?.column_id),
        is_read_message: new FormControl(this.channel?.is_read_message),
      });
      if (this.channelForm.controls.funnel_id.value) {
        this.getColumnsByFunnel(this.channelForm.controls.funnel_id.value, true);
      }
    } else {
      this.channelForm = new FormGroup({
        name: new FormControl('', [Validators.required, trimValidator]),
        type: new FormControl('', [Validators.required]),
        ...(this.canSelectDefaultResponsible ? { responsible_ids: new FormControl<string[]>([], { nonNullable: true }) } : {}),
        funnel_id: new FormControl(''),
        column_id: new FormControl(''),
        is_read_message: new FormControl(true),
      });

      this.isLoader = false;
    }

    if (this.channel?.type === CHANNEL_TYPE.OLX) {
      this.channelForm.addControl('is_notification_delivery', new FormControl(this.channel.is_notification_delivery));
    }

    this.onCreateGroupFormValueChange();
  }

  private onCreateGroupFormValueChange(): void {
    if (!this.channel_id) return;

    this.initialFormValue = this.channelForm.getRawValue();

    this.channelForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.isHasChange = !_.isEqual(this.channelForm.getRawValue(), this.initialFormValue);
    });
  }

  protected get responsibleOptions(): ChannelResponsibleEmployee[] {
    const selected = this.channelForm?.controls.responsible_ids?.value || [];
    return this.managersList.filter((employee) => !employee.disabled || selected.includes(employee.id));
  }

  protected loadResponsibleEmployees(): void {
    if (!this.canSelectDefaultResponsible && !this.channel?.channel_group_id) return;
    this.responsibleLoading = true;
    this.responsibleLoadError = false;
    this.channelService
      .getResponsibleEmployees()
      .pipe(finalize(() => { this.responsibleLoading = false; }), takeUntil(this.destroy$))
      .subscribe({
        next: (employees) => { this.managersList = employees; },
        error: () => {
          this.responsibleLoadError = true;
        },
      });
  }

  private onGetChannel(): void {
    this.integrationService.getChannel(this.channel_id).subscribe({
      next: (v) => {
        this.isLoader = false;

        this.channel = v;
        this.onInitForm();
        if (!this.canSelectDefaultResponsible && this.channel?.channel_group_id) this.loadResponsibleEmployees();
      },
      error: (e) => {
        this.isLoader = false;

        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
        });

        this.onCloseSidebar();
      },
    });
  }

  public onCloseSidebar(): void {
    this.close.emit('close');
  }

  protected openChannelGroup(): void {
    const groupId = this.channel?.channel_group_id;
    if (!groupId || !this.canSelectDefaultResponsible || this.openingGroup) return;

    this.openingGroup = true;
    this.channelService.getChannelGroups()
      .pipe(
        switchMap((groups: IChannelGroup[]) => {
          const group = groups.find((item) => item.id === groupId);
          if (!group) throw new Error('Channel group not found');
          return this.stackedSidebarService.openSidebar<IChannelGroup>(
            { type: SidebarType.ChannelGroupForm, width: '520px' },
            { group },
          );
        }),
        switchMap((saved) => saved ? this.integrationService.getChannel(this.channel_id) : EMPTY),
        finalize(() => { this.openingGroup = false; }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (channel) => {
          this.channel = { ...this.channel, channel_group_id: channel.channel_group_id, responsible_ids: channel.responsible_ids };
          const control = this.channelForm.controls.responsible_ids;
          control?.setValue(channel.responsible_ids || [], { emitEvent: false });
          if (channel.channel_group_id) control?.disable({ emitEvent: false });
          else control?.enable({ emitEvent: false });
          if (this.initialFormValue) this.initialFormValue.responsible_ids = [...(channel.responsible_ids || [])];
          this.isHasChange = !_.isEqual(this.channelForm.getRawValue(), this.initialFormValue);
          this.loadResponsibleEmployees();
        },
        error: () => this.notificationService.showError('', activeTranslations.chat_settings.group_error),
      });
  }

  public onSave(): void {
    if (this.responsibleLoading || this.responsibleLoadError) return;
    this.channelForm.markAllAsTouched();
    if (this.channelForm.valid) {
      const payload = this.channelForm.getRawValue();
      this.integrationService
        .createChannel(payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.close.emit(SUCCESS_RESPONSE);
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_created,
            );
          },
          error: (e: any) => {
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
            });
          },
        });
    }
  }

  public onSaveChange(): void {
    if (this.responsibleLoading || this.responsibleLoadError) return;
    this.channelForm.markAllAsTouched();

    if (this.channelForm.valid) {
      const payload = this.channelForm.getRawValue();
      if (this.channel?.channel_group_id) delete payload.responsible_ids;
      this.integrationService
        .updateChannel(this.channel.id, payload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.close.emit(SUCCESS_RESPONSE);
            this.notificationService.showSuccess(
              '',
              activeTranslations.notifications_texts.success.successfully_updated,
            );
          },
          error: (e: any) => {
            (e.error?.errors || []).forEach((err: IError) => {
              this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
            });
          },
        });
    }
  }

  private onGetAllFunnels(): void {
    this.boardService
      .getFunnels({ type: FUNNEL_TYPE.LEAD })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (funnels) => {
          this.allowedFunnels = [...funnels] as unknown as IFunnel[];
        },
        error: (e) => {
          (e.error?.errors || []).forEach((err: IError) => {
            this.notificationService.showError('', ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
          });
        },
      });
  }

  protected getColumnsByFunnel(event: any, keepSelectedColumn = false): void {
    if (!event) {
      this.allowedColumns = [];
      this.channelForm.controls.column_id.setValue('');
      return;
    }

    const selectedColumnId = this.channelForm.controls.column_id.value;
    this.boardService
      .getAllColumns(event)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (columns: any[]) => {
          this.allowedColumns = [...columns];
          if (keepSelectedColumn && this.allowedColumns.some((column) => column.id === selectedColumnId)) {
            this.channelForm.controls.column_id.setValue(selectedColumnId);
          } else if (this.allowedColumns.length) {
            this.channelForm.controls.column_id.setValue(this.allowedColumns[0].id);
          } else {
            this.channelForm.controls.column_id.setValue('');
          }
        },
      });
  }

  protected selectDisable(event: any): void {
    if (event !== CHANNEL_TYPE.OLX) {
      this.channelForm.removeControl('is_notification_delivery');
    }

    if (event === CHANNEL_TYPE.WIDGET) {
      this.channelForm.controls.is_read_message.setValue(true);
      this.isDisabled = true;
    } else if (event === CHANNEL_TYPE.OLX) {
      this.channelForm.addControl('is_notification_delivery', new FormControl(false));
    } else {
      this.isDisabled = false;
    }
  }
}
